// ============================================================================
// STUDIO CANETTE — ÉTAT DE SESSION
//
// Une seule vérité pour « qui est connecté, sur quel espace, avec combien de
// crédits ». L'en-tête, les gardes du routeur, le studio et la page Compte
// lisent tous le même objet et s'abonnent au même flux : sans ça, la jauge de
// crédits et le studio divergent dès la première génération.
//
// `state` est délibérément MUTABLE et partagé. On ne recrée pas d'objet à
// chaque changement : les abonnés reçoivent la même référence et relisent ce
// dont ils ont besoin. C'est ce qui permet à `setCredits()` de faire bouger le
// compteur de l'en-tête en une ligne, sans re-fetch.
//
// ── QUATRE RÈGLES DE L'AUTHENTIFICATION POCKETBASE ──────────────────────────
//
//  • `pb.authStore.onChange` EST SYNCHRONE. C'est une simple notification émise
//    par un magasin adossé à `localStorage` : aucun verrou interne, aucune
//    ré-entrance à craindre, on peut y rappeler n'importe quelle méthode du
//    client. La seule règle est donc de ne rien ATTENDRE dans ce callback : on
//    lance le travail asynchrone et on rend la main tout de suite, sinon le
//    magasin resterait suspendu à une promesse pendant que l'interface, elle,
//    attend déjà l'événement suivant.
//
//  • LE JETON NE SE RENOUVELLE PAS TOUT SEUL. Le SDK PocketBase ne rafraîchit
//    rien en arrière-plan : c'est à nous. On appelle `authRefresh()` au
//    démarrage puis toutes les 30 minutes. Un échec explicite du serveur
//    (401/403/404) = session morte : on purge et on sort l'utilisateur de
//    l'écran protégé. Un échec réseau ou 5xx ne touche à RIEN — déconnecter
//    quelqu'un parce que le Wi-Fi a hoqueté est la pire réponse possible.
//
//  • LA RÉINITIALISATION DE MOT DE PASSE N'OUVRE PAS DE SESSION. Le lien du
//    courriel n'apporte qu'un JETON À USAGE UNIQUE dans l'URL
//    (`/nouveau-mdp?token=…`) : l'utilisateur reste un visiteur jusqu'à ce
//    qu'il ait choisi son mot de passe. `recoveryMode()` est vrai tant que ce
//    jeton est présent et non consommé — c'est lui qui autorise l'écran
//    /nouveau-mdp —, et `updatePassword()` le relit pour appeler
//    `confirmPasswordReset(token, mdp, mdp)`. Conséquence à connaître : changer
//    le mot de passe fait tourner la clé de jeton côté serveur, donc invalide
//    toute session déjà ouverte sur ce compte — on reconnecte l'utilisateur
//    dans la foulée avec l'adresse portée par le jeton.
//
//  • CHANGER SON MOT DE PASSE EN ÉTANT CONNECTÉ NE PASSE PAS PAR ICI.
//    PocketBase exige l'ancien mot de passe pour une mise à jour directe, et la
//    collection `users` a `updateRule = null` (ARCHITECTURE.md § 5.2) : le
//    navigateur n'a pas le droit d'écrire son propre enregistrement. Le seul
//    chemin est le courriel de réinitialisation — la page Compte appelle donc
//    `resetPassword(state.user.email)`, pas `updatePassword`.
//
// Les messages d'erreur sortent d'ici DÉJÀ TRADUITS : les pages les affichent
// tels quels, sans jamais interpréter un libellé anglais de PocketBase.
// ============================================================================

import { getTokenPayload, isTokenExpired } from 'pocketbase';

import { pb, hasPB } from './pb.js';
import { api, ApiError } from './api.js';
import { featuresFor } from '../../shared/plans.js';

/* ------------------------------------------------------------------ *
 * L'état                                                              *
 * ------------------------------------------------------------------ */

/**
 * @typedef {Object} SessionState
 * @property {Object|null} user       compte courant (id, email, name, displayName, company, verified)
 * @property {Object|null} workspace  espace de facturation renvoyé par /api/me
 * @property {string} plan            'free' | 'solo' | 'studio' | 'agence'
 * @property {Object} features        droits dérivés du plan (voir shared/plans.js)
 * @property {{balance:number, monthly:number, resetAt:?string, unlimited:boolean}} credits
 * @property {boolean} loading        vrai tant que bootSession() n'a pas tranché
 * @property {?string} error          dernier incident non bloquant
 */

/** @type {SessionState} */
export const state = {
  user: null,
  workspace: null,
  plan: 'free',
  features: featuresFor('free'),
  credits: { balance: 0, monthly: 0, resetAt: null, unlimited: false },
  isSuperAdmin: false,
  imageRoutes: null,
  loading: true,
  error: null
};

const subs = new Set();

/**
 * S'abonne aux changements de session. Le callback est appelé TOUT DE SUITE
 * avec l'état courant : un composant n'a pas à gérer séparément « au montage »
 * et « à la mise à jour ».
 * @returns {() => void} désabonnement
 */
export function onSession(fn) {
  if (typeof fn !== 'function') return () => {};
  subs.add(fn);
  try { fn(state); } catch (e) { console.error('[session] abonné', e); }
  return () => subs.delete(fn);
}

function emit() {
  for (const fn of [...subs]) {
    try { fn(state); } catch (e) { console.error('[session] abonné', e); }
  }
}

export const isAuthed = () => Boolean(state.user);

const PAS_CONFIG = "L'authentification n'est pas configurée sur cette instance.";

/* ------------------------------------------------------------------ *
 * Forme de l'utilisateur                                              *
 * ------------------------------------------------------------------ */

/**
 * Un enregistrement PocketBase porte `name` ; /api/me renvoie `displayName`.
 * On expose les deux, sinon chaque page devrait connaître la provenance de son
 * utilisateur — et l'en-tête afficherait un nom différent avant et après
 * l'appel à /api/me.
 */
function toUser(rec) {
  if (!rec || !rec.id) return null;
  const nom = String(rec.name ?? '').trim();
  return {
    id: rec.id,
    email: String(rec.email ?? ''),
    name: nom,
    displayName: nom,
    company: String(rec.company ?? ''),
    avatar: String(rec.avatar ?? ''),
    verified: Boolean(rec.verified)
  };
}

/**
 * Applique un enregistrement PocketBase par-dessus l'état courant.
 * `authRefresh` renvoie l'enregistrement brut, sans les champs que /api/me a
 * ajoutés (`displayName`, `company` mis en forme…) : écraser bêtement
 * effacerait le nom affiché à chaque rafraîchissement de jeton. On ne recopie
 * donc que ce que PocketBase renseigne réellement — et on repart de zéro si ce
 * n'est plus le même compte.
 */
function fusionUser(rec) {
  const frais = toUser(rec);
  if (!frais) return null;
  if (state.user?.id !== frais.id) return frais;

  const out = { ...state.user };
  for (const [k, v] of Object.entries(frais)) {
    if (v !== '' && v != null) out[k] = v;
  }
  return out;
}

function resetGuest() {
  state.user = null;
  state.workspace = null;
  state.plan = 'free';
  state.features = featuresFor('free');
  state.credits = { balance: 0, monthly: 0, resetAt: null, unlimited: false };
  state.isSuperAdmin = false;
  state.imageRoutes = null;
}

/* ------------------------------------------------------------------ *
 * Mode récupération de mot de passe                                   *
 * ------------------------------------------------------------------ */

/** Trois segments base64url séparés par des points : la forme d'un JWT. */
const JWT = /^[\w-]+\.[\w-]+\.[\w-]+$/;

let recoveryTok = null;
let recoveryConsomme = false;

/**
 * Le jeton de réinitialisation arrive dans l'URL, posé par le gabarit d'e-mail
 * de PocketBase (`{APP_URL}/nouveau-mdp?token={TOKEN}`).
 *
 * On ne se contente pas de la présence d'un `?token=` : une URL de fichier
 * signée en porte un aussi. On vérifie la forme JWT, la présence d'un `id`, et
 * on rejette tout jeton dont le type annoncé n'est pas `passwordReset`.
 */
function lireJetonUrl() {
  try {
    const t = new URLSearchParams(location.search).get('token') || '';
    if (!JWT.test(t)) return null;
    const p = getTokenPayload(t);
    if (!p || !p.id) return null;
    if (p.type && p.type !== 'passwordReset') return null;
    return t;
  } catch {
    return null;
  }
}

/**
 * Relu à la demande : le module est chargé une fois au démarrage, mais on peut
 * arriver sur /nouveau-mdp?token=… par une navigation interne, sans rechargement.
 */
function jetonRecuperation() {
  if (recoveryConsomme) return null;
  if (!recoveryTok) recoveryTok = lireJetonUrl();
  return recoveryTok;
}

export const recoveryMode = () => Boolean(jetonRecuperation());

/* ------------------------------------------------------------------ *
 * Entretien du jeton                                                  *
 * ------------------------------------------------------------------ */

/** 30 min : le jeton PocketBase vit 7 jours par défaut, on garde large. */
const REFRESH_MS = 30 * 60 * 1000;

let refreshTimer = null;
let refreshing = null;
let lastRefreshAt = 0;

/** Préfixes protégés du routeur (voir la table de main.js).
 *  Dupliqué ici à dessein : le routeur ne publie pas sa table, et une session
 *  qui meurt doit sortir l'utilisateur de l'écran où il travaille tout de
 *  suite, pas attendre son prochain clic. */
const PROTEGE = /^\/(app|compte|merci)\b/;

/**
 * Une erreur du SDK qui ne prouve PAS que le jeton est mort : réseau coupé,
 * requête abandonnée, PocketBase en train de redémarrer. On garde la session.
 */
function estTerminale(err) {
  const s = Number(err?.status ?? 0);
  return s === 401 || s === 403 || s === 404;
}

async function sortieSessionMorte() {
  if (!PROTEGE.test(location.pathname)) return;
  const suite = location.pathname + location.search + location.hash;
  const to = '/connexion?suite=' + encodeURIComponent(suite);
  try {
    // Import différé : router.js importe ce module. Un import statique dans
    // l'autre sens créerait un cycle au chargement.
    const { navigate } = await import('./router.js');
    navigate(to, { replace: true });
  } catch {
    location.assign(to);
  }
}

/**
 * Renouvelle le jeton auprès de PocketBase.
 * @returns {Promise<boolean>} faux uniquement si la session est morte pour de bon
 */
function refreshToken() {
  if (!hasPB || !pb.authStore.token) return Promise.resolve(false);
  if (refreshing) return refreshing;               // deux appels concurrents s'annuleraient l'un l'autre

  refreshing = (async () => {
    try {
      await pb.collection('users').authRefresh();
      lastRefreshAt = Date.now();
      return true;
    } catch (e) {
      if (!estTerminale(e)) {
        console.warn('[session] authRefresh différé', e?.message || e);
        return true;                               // incident passager : on ne déconnecte personne
      }
      pb.authStore.clear();                        // → handleAuthChange purge l'état et redirige
      return false;
    } finally {
      refreshing = null;
    }
  })();

  return refreshing;
}

function onVisible() {
  if (document.visibilityState !== 'visible') return;
  // Un onglet en arrière-plan voit ses minuteries ralenties par le navigateur :
  // au retour, on se fie à l'horloge, pas à l'intervalle.
  if (Date.now() - lastRefreshAt >= REFRESH_MS) refreshToken();
}

function startAutoRefresh() {
  if (!hasPB || refreshTimer) return;
  refreshTimer = setInterval(refreshToken, REFRESH_MS);
  document.addEventListener('visibilitychange', onVisible);
  // Le minuteur reste en place après une déconnexion : `refreshToken()` sort
  // immédiatement s'il n'y a pas de jeton, et une reconnexion dans le même
  // onglet est ainsi couverte sans rien réarmer.
}

/* ------------------------------------------------------------------ *
 * Démarrage                                                           *
 * ------------------------------------------------------------------ */

let bootPromise = null;

/**
 * Résout la session avant le premier rendu. Idempotent : le deuxième appel
 * renvoie la même promesse, pour que le routeur puisse l'attendre sans risque.
 */
export function bootSession() {
  if (bootPromise) return bootPromise;

  bootPromise = (async () => {
    if (!hasPB) {
      // Mode démo : pas d'authentification possible, mais la navigation doit
      // fonctionner. On sort de `loading` immédiatement.
      resetGuest();
      state.loading = false;
      emit();
      return state;
    }

    // Jeton présent mais périmé selon sa propre date d'expiration : inutile de
    // demander au serveur ce qu'on sait déjà.
    if (pb.authStore.token && !pb.authStore.isValid) pb.authStore.clear();

    // État optimiste à partir du stockage local, AVANT l'aller-retour réseau :
    // l'en-tête affiche le bon compte dès la première frame.
    if (pb.authStore.record) state.user = toUser(pb.authStore.record);

    // Abonnement posé après cet état optimiste, pour que le premier événement
    // (celui de l'authRefresh ci-dessous) ne soit pas vu comme un changement de
    // compte et ne déclenche pas un /api/me en double.
    pb.authStore.onChange(handleAuthChange);

    if (state.user) {
      const vivante = await refreshToken();
      if (vivante && pb.authStore.token) {
        await refreshMe();
      } else {
        resetGuest();
      }
    } else {
      resetGuest();
    }

    startAutoRefresh();
    state.loading = false;
    emit();
    return state;
  })();

  return bootPromise;
}

/**
 * Notification du magasin d'authentification. Trois déclencheurs :
 * connexion/rafraîchissement (jeton écrit), déconnexion (jeton effacé), et
 * — c'est le cadeau de PocketBase — la même opération faite dans UN AUTRE
 * ONGLET, via l'événement `storage`. Se déconnecter quelque part déconnecte
 * partout, gratuitement.
 *
 * Callback synchrone : on lance, on n'attend pas (voir le bandeau).
 */
function handleAuthChange(token, record) {
  if (!token || !record) {
    if (!state.user) return;                 // déjà invité : rien à annoncer
    resetGuest();
    emit();
    if (!deconnexionVolontaire) sortieSessionMorte();
    return;
  }

  const change = record.id !== state.user?.id;
  state.user = fusionUser(record);
  emit();
  if (change) refreshMe();
}

/* ------------------------------------------------------------------ *
 * Profil et crédits                                                   *
 * ------------------------------------------------------------------ */

let mePromise = null;

/**
 * Relit /api/me. Déduplique les appels concurrents : la connexion déclenche à
 * la fois un appel explicite et un événement du magasin, ce qui ferait deux
 * requêtes pour rien.
 */
export function refreshMe() {
  if (!isAuthed()) { resetGuest(); emit(); return Promise.resolve(state); }
  if (mePromise) return mePromise;

  mePromise = (async () => {
    try {
      const me = await api('/me');
      applyMe(me);
      state.error = null;
    } catch (e) {
      // Sur 401, la couche API a déjà purgé la session et redirigé : inutile
      // d'afficher une erreur par-dessus.
      if (!(e instanceof ApiError && e.status === 401)) {
        state.error = e?.message || 'Profil indisponible.';
        console.error('[session] /me', e);
      }
    } finally {
      mePromise = null;
    }
    emit();
    return state;
  })();

  return mePromise;
}

function applyMe(me) {
  if (!me || typeof me !== 'object') return;
  if (me.user) state.user = { ...state.user, ...me.user };
  state.workspace = me.workspace ?? null;
  state.plan = me.plan ?? me.workspace?.plan ?? 'free';
  state.features = me.features ?? featuresFor(state.plan);
  const c = me.credits ?? {};
  state.credits = {
    balance: Number(c.balance ?? 0),
    monthly: Number(c.monthly ?? 0),
    resetAt: c.resetAt ?? null,
    // Compte d'exploitation : le solde ne bouge pas, l'interface doit dire
    // « illimité » plutôt qu'afficher un décompte figé qu'on croirait bloqué.
    unlimited: Boolean(c.unlimited)
  };
  state.isSuperAdmin = Boolean(me.isSuperAdmin);
  // `null` pour un compte client : le serveur ne lui envoie pas la liste des
  // fournisseurs, et le sélecteur du studio ne s'affiche donc pas. Ce n'est pas
  // qu'une question d'interface — choisir son modèle est un levier de coût.
  state.imageRoutes = me.imageRoutes ?? null;
}

/**
 * Mise à jour optimiste du solde après une génération. /api/generate renvoie le
 * nouveau solde : le recalculer par un /api/me ferait clignoter la jauge une
 * seconde après coup, ce qui donne l'impression d'un débit en double.
 */
export function setCredits(balance) {
  const n = Number(balance);
  if (!Number.isFinite(n)) return;
  state.credits = { ...state.credits, balance: Math.max(0, n) };
  emit();
}

/* ------------------------------------------------------------------ *
 * Traduction des erreurs PocketBase                                   *
 *                                                                     *
 * Une `ClientResponseError` porte `status`, et `response = {message,   *
 * data:{champ:{code,message}}}`. Les erreurs de champ sont les plus    *
 * précises : on les lit d'abord, le message global ensuite.            *
 * ------------------------------------------------------------------ */

const MESSAGES = [
  [/failed to authenticate/i, 'E-mail ou mot de passe incorrect.'],
  [/not verified|must be verified|verified account/i,
    "Cette adresse n'est pas encore vérifiée — le lien est dans votre boîte mail."],
  [/requires valid record authorization|missing or invalid.*token|invalid or expired token/i,
    'Votre session a expiré. Reconnectez-vous.'],
  [/too many requests|rate.?limit/i, 'Trop de tentatives. Réessayez dans une minute.'],
  [/missing or invalid provider/i, "La connexion Google n'est pas activée sur cette instance."],
  [/state parameters don't match|oauth2 redirect error/i, 'La connexion Google a échoué. Réessayez.'],
  [/realtime connection interrupted/i, "La connexion avec le service d'authentification a été interrompue."],
  [/only superusers/i, "Action réservée à l'administration."],
  [/failed to connect to the pocketbase server|failed to fetch|networkerror|load failed/i,
    "Service d'authentification injoignable."],
  [/failed to create record/i, 'Inscription refusée : vérifiez les informations saisies.'],
  [/failed to update record/i, 'Modification refusée : vérifiez les informations saisies.']
];

function frChamp(champs) {
  const code = nom => String(champs[nom]?.code || '');
  const texte = nom => String(champs[nom]?.message || '');

  if (champs.identity) return 'E-mail ou mot de passe incorrect.';

  if (champs.email) {
    const c = code('email');
    if (/not_unique/.test(c) || /already in use/i.test(texte('email'))) {
      return 'Un compte existe déjà avec cette adresse — connectez-vous, ou demandez un nouveau mot de passe.';
    }
    if (/required/.test(c)) return 'Renseignez votre adresse e-mail.';
    // PocketBase mélange volontairement « invalide » et « déjà prise » sur ce
    // code, pour ne pas révéler qui a un compte. On reste aussi ambigu que lui.
    if (/invalid_email/.test(c)) return 'Adresse invalide ou déjà utilisée.';
    return 'Adresse e-mail invalide.';
  }

  if (champs.password) {
    const c = code('password');
    if (/length|min|short/.test(c)) return 'Mot de passe trop court : 8 caractères minimum.';
    if (/required/.test(c)) return 'Choisissez un mot de passe.';
    return 'Mot de passe refusé : 8 caractères minimum.';
  }

  if (champs.passwordConfirm) return 'Les deux mots de passe ne correspondent pas.';
  if (champs.token) return 'Ce lien a expiré. Demandez-en un nouveau.';

  return null;
}

/** @returns {?string} message français prêt à afficher */
function frError(err) {
  if (!err) return null;
  if (err.isAbort) return 'Requête interrompue. Réessayez.';

  const rep = err.response || err.data || {};
  const champs = rep.data && typeof rep.data === 'object' ? rep.data : {};

  const parChamp = frChamp(champs);
  if (parChamp) return parChamp;

  const brut = String(rep.message || err.message || '');
  for (const [re, fr] of MESSAGES) if (re.test(brut)) return fr;

  const s = Number(err.status ?? 0);
  if (s === 0) return "Service d'authentification injoignable.";
  if (s === 429) return 'Trop de tentatives. Réessayez dans une minute.';
  if (s >= 500) return "Le service d'authentification est en panne. Réessayez dans un instant.";

  return brut || "L'authentification a échoué.";
}

/* ------------------------------------------------------------------ *
 * Actions d'authentification                                          *
 *                                                                     *
 * Toutes renvoient { error?: string } avec un message DÉJÀ en français.*
 * ------------------------------------------------------------------ */

let deconnexionVolontaire = false;

export async function signIn(email, password) {
  if (!hasPB) return { error: PAS_CONFIG };
  try {
    const { record } = await pb.collection('users')
      .authWithPassword(String(email || '').trim(), String(password || ''));
    // `handleAuthChange` a déjà posé l'utilisateur et lancé /api/me ; on
    // l'attend quand même, pour que la page qui redirige vers /app y trouve un
    // solde de crédits et pas un compteur à zéro.
    state.user = fusionUser(record);
    lastRefreshAt = Date.now();
    startAutoRefresh();
    await refreshMe();
    return {};
  } catch (e) {
    return { error: frError(e) };
  }
}

/**
 * Création de compte.
 *
 * Trois appels, dans cet ordre, et aucun n'est facultatif :
 *  1. `create()` — PocketBase exige `passwordConfirm` dans le corps, sans quoi
 *     il refuse l'enregistrement ;
 *  2. `requestVerification()` — PocketBase n'envoie RIEN de lui-même : sans cet
 *     appel, personne ne reçoit jamais de courriel de vérification ;
 *  3. `authWithPassword()` — on laisse l'utilisateur entrer et travailler tout
 *     de suite. La vérification d'adresse n'est pas un mur, c'est un rappel :
 *     PocketBase n'exige une adresse vérifiée que si la collection porte une
 *     règle d'authentification, ce que la nôtre ne fait pas.
 *
 * Le retour dit exactement ce qui s'est passé, parce que l'écran d'après en
 * dépend : `authed` décide entre « votre espace est prêt » et « connectez-vous »,
 * `verificationSent` décide d'annoncer un courriel — on ne promet jamais un
 * e-mail dont on n'a pas la preuve qu'il est parti.
 *
 * @param {Object} [meta] {name, company} → champs de la collection `users`
 * @returns {Promise<{error?:string, authed:boolean, verificationSent:boolean}>}
 */
export async function signUp(email, password, meta = {}) {
  if (!hasPB) return { error: PAS_CONFIG, authed: false, verificationSent: false };

  const mail = String(email || '').trim();
  const pass = String(password || '');
  const corps = {
    email: mail,
    password: pass,
    passwordConfirm: pass,
    name: String(meta.name ?? meta.displayName ?? '').trim(),
    company: String(meta.company ?? '').trim()
  };

  try {
    await pb.collection('users').create(corps);
  } catch (e) {
    return { error: frError(e), authed: false, verificationSent: false };
  }

  let verificationSent = true;
  try {
    await pb.collection('users').requestVerification(mail);
  } catch (e) {
    // Le compte existe : un courriel non parti ne doit pas ressembler à un
    // échec d'inscription. On le dit, sans en faire une erreur bloquante.
    verificationSent = false;
    console.warn('[session] courriel de vérification non envoyé', e?.message || e);
  }

  try {
    const { record } = await pb.collection('users').authWithPassword(mail, pass);
    state.user = fusionUser(record);
    lastRefreshAt = Date.now();
    startAutoRefresh();
    await refreshMe();
  } catch (e) {
    console.error('[session] connexion automatique après inscription', e);
    return {
      authed: false,
      verificationSent,
      error: 'Compte créé, mais la connexion automatique a échoué. Connectez-vous avec vos identifiants.'
    };
  }

  return { authed: true, verificationSent };
}

/**
 * Renvoie le courriel de vérification d'adresse.
 *
 * À NE PAS confondre avec un second `signUp` : PocketBase refuserait la
 * création avec `validation_not_unique` puisque le compte existe déjà. Ici on
 * ne demande qu'un nouvel envoi ; la réponse est la même que l'adresse existe
 * ou non, et qu'elle soit déjà vérifiée ou pas.
 */
export async function resendVerification(email) {
  if (!hasPB) return { error: PAS_CONFIG };
  try {
    await pb.collection('users').requestVerification(String(email || '').trim());
    return {};
  } catch (e) {
    return { error: frError(e) };
  }
}

/**
 * Connexion Google.
 *
 * `authWithOAuth2` ouvre une fenêtre surgissante et se résout tout seul : le
 * SDK écoute le retour par une connexion temps réel côté PocketBase. Il n'y a
 * donc AUCUNE redirection à intercepter au chargement de l'application.
 *
 * Deux pièges :
 *  • la fenêtre est ouverte de façon synchrone au tout début de l'appel — cette
 *    fonction doit donc être appelée depuis un gestionnaire de clic, sinon le
 *    navigateur bloque la fenêtre ;
 *  • si l'utilisateur ferme la fenêtre, le SDK ne rejette rien : la promesse
 *    reste en attente pour toujours et le bouton reste figé. D'où le garde-fou
 *    ci-dessous. Si la promesse aboutit malgré tout après coup, le magasin
 *    d'authentification préviendra `handleAuthChange` et la session s'ouvrira
 *    normalement — rien n'est perdu.
 */
const OAUTH_TIMEOUT_MS = 3 * 60 * 1000;

function avecDelai(promesse, ms, message) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(message)), ms);
    promesse.then(
      v => { clearTimeout(t); resolve(v); },
      e => { clearTimeout(t); reject(e); }
    );
  });
}

export async function signInWithGoogle() {
  if (!hasPB) return { error: PAS_CONFIG };
  try {
    const { record } = await avecDelai(
      pb.collection('users').authWithOAuth2({ provider: 'google' }),
      OAUTH_TIMEOUT_MS,
      "La connexion Google n'a pas abouti : la fenêtre a été fermée ou bloquée par le navigateur."
    );
    state.user = fusionUser(record);
    lastRefreshAt = Date.now();
    startAutoRefresh();
    await refreshMe();
    return {};
  } catch (e) {
    return { error: frError(e) };
  }
}

/**
 * Déconnexion. Purement locale : un jeton PocketBase est un JWT autoporteur,
 * il n'y a pas de session à révoquer côté serveur — l'effacer du stockage
 * suffit, et prévient les autres onglets par l'événement `storage`.
 */
export async function signOut() {
  recoveryConsomme = true;
  // `clear()` prévient `handleAuthChange` de façon synchrone : ce drapeau lui
  // dit que le départ est volontaire et qu'il n'a pas à rediriger vers
  // /connexion — la page appelante sait où elle veut aller.
  deconnexionVolontaire = true;
  try { pb.authStore.clear(); } catch (e) { console.error('[session] signOut', e); }
  deconnexionVolontaire = false;

  resetGuest();
  emit();
  return {};
}

/**
 * Envoie le courriel de réinitialisation.
 * PocketBase répond de la même façon que l'adresse existe ou non : la page doit
 * donc dire « si un compte existe à cette adresse… », jamais « e-mail envoyé ».
 */
export async function resetPassword(email) {
  if (!hasPB) return { error: PAS_CONFIG };
  try {
    await pb.collection('users').requestPasswordReset(String(email || '').trim());
    return {};
  } catch (e) {
    return { error: frError(e) };
  }
}

/**
 * Pose le nouveau mot de passe au retour du lien de récupération.
 *
 * Le jeton vient de l'URL (`/nouveau-mdp?token=…`) — voir `recoveryMode()`. Il
 * est à usage unique : une fois consommé, `recoveryMode()` redevient faux même
 * si le paramètre traîne encore dans la barre d'adresse.
 */
export async function updatePassword(newPassword) {
  if (!hasPB) return { error: PAS_CONFIG };

  const pass = String(newPassword || '');
  const tok = jetonRecuperation();
  if (!tok) {
    return { error: 'Lien de réinitialisation absent. Demandez-en un nouveau depuis « Mot de passe oublié ».' };
  }
  if (isTokenExpired(tok)) {
    return { error: 'Ce lien a expiré. Demandez-en un nouveau.' };
  }

  try {
    await pb.collection('users').confirmPasswordReset(tok, pass, pass);
  } catch (e) {
    return { error: frError(e) };
  }

  recoveryConsomme = true;
  recoveryTok = null;

  // Changer le mot de passe fait tourner la clé de jeton côté PocketBase :
  // toutes les sessions ouvertes de ce compte, y compris celle de cet onglet,
  // sont désormais invalides. On enchaîne donc sur une vraie connexion, avec
  // l'adresse portée par le jeton — sinon l'utilisateur atterrit sur /app pour
  // se faire aussitôt renvoyer vers /connexion.
  const mail = String(getTokenPayload(tok)?.email || '').trim();
  if (mail) {
    try {
      const { record } = await pb.collection('users').authWithPassword(mail, pass);
      state.user = fusionUser(record);
      lastRefreshAt = Date.now();
      startAutoRefresh();
      await refreshMe();
      return {};
    } catch (e) {
      console.warn('[session] reconnexion après réinitialisation', e?.message || e);
    }
  }

  // Reconnexion impossible (jeton sans adresse, ou compte soumis à une règle
  // d'authentification) : on repart proprement de l'écran de connexion.
  deconnexionVolontaire = true;
  try { pb.authStore.clear(); } catch { /* rien à purger */ }
  deconnexionVolontaire = false;
  resetGuest();
  emit();
  return {};
}
